/**
 * KisiMeslekleriKarti.jsx — bir kişinin MESLEKLERİ (backlog #466, backend #464: `person_meslek`).
 *
 * Meslek KİŞİNİNDİR: hangi fuarda ya da hangi firmada olursa olsun aynı. Bu kart iki yerde aynı işi görür:
 * Kişi Detay › İSG Belgeleri › Meslek (SAKA personeli) ve firma portalının personel sayfası. Her meslek bir satır:
 *   · adı + kategorisi + KİMİN eklediği (SAKA / firma / kişinin kendisi; başka firmanın adı gizli),
 *   · "Onay bekliyor" — yalnızca meslek kuralı Çalışan setinden hafifse (`kartMeslekleri[].hafifKural`) ve personel henüz onaylamadıysa,
 *   · kartın o meslekle basılıp basılamadığı ve eksikleri — BACKEND'in `kartMeslekleri[]` kararı olduğu gibi, burada türetilmez,
 *   · mesleğin kendi belge seti,
 *   · (firma sayfası) mesleğin Mesleki Eğitim sertifikası: `renderSlot`.
 * Meslekler tek tek eklenir (seçici → POST) ve tek tek kaldırılır (× → DELETE): eskisi gibi "tüm listeyi yaz" (PATCH) değil —
 * çünkü bir firma yalnızca KENDİ eklediğini, onaylı sertifikası yokken kaldırabilir. Personel her şeyi kaldırabilir.
 *
 * Mesleki Eğitim eksikliği kartı ENGELLEMEZ (#464): kart "Çalışan" olarak basılır; bu bilgi satırı KartUygunsuzluklari'nda.
 *
 * @param personId        kişi
 * @param meslekler       `kisiMeslekleri(satır)`: kişinin meslekleri (kaynak, ekleyenFirmaAdi, onayli, kategoriAdi...)
 * @param kartSatiri      `kartMeslekleri[]` taşıyan satır (kart kararının hesaplandığı firma girdisi); yoksa null → kart durumu yazılmaz
 * @param yuvalar         `meslekiEgitimler[]`: onaylı sertifikası olan meslek firma tarafından kaldırılamaz
 * @param calisanBelgeleri  Çalışan olarak basılırken istenen belgeler (`belgeKurali.belgeler`)
 * @param taraf           "denetmen" (SAKA personeli: onaylar, her şeyi kaldırır) | "firma"
 * @param yazabilir       ekle / kaldır / onayla düğmeleri (firma: PERSONEL modülü WRITE; ilişki bitmişse false)
 * @param renderSlot      (meslek) => node — satırın altına (firma sayfasında sertifika kartı)
 * @param onChanged       (mesaj) => void — yazma bitince; çağıran sayfayı yeniden okur
 * @param onError         (mesaj) => void
 * @param bare            true → Card çerçevesi ve başlık yok (bir pencerenin gövdesinde)
 * @param children        kartın altına (ör. "Meslek Kuralları" bağlantısı)
 */
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, CardBody, CardHeader, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { Briefcase, CheckCircle2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { addKisiMeslegi, getMeslekList, onaylaKisiMeslegi, removeKisiMeslegi } from "../../services/kisiAdminService";
import { MeslekSecenekleri } from "./MeslekSecici";
import { kartMeslegiOf, meslekKaynakMetni } from "./meslekler";
import { meslekEksikAdi } from "./KartUygunsuzluklari";

export default function KisiMeslekleriKarti({
  personId, meslekler = [], kartSatiri = null, yuvalar = null, calisanBelgeleri = [], taraf = "firma", yazabilir = false,
  renderSlot = null, onChanged, onError, bare = false, children = null,
}) {
  const { t } = useTranslation("components");
  const personel = taraf === "denetmen";
  const [katalog, setKatalog] = useState([]);
  const [secim, setSecim] = useState("");
  const [busy, setBusy] = useState(null); // "ekle" | `kaldir-${id}` | `onayla-${id}`
  const [silinecek, setSilinecek] = useState(null);

  // Katalog: kaldırılmış olmayan tüm meslekler (kategori ağacıyla). Yalnızca yazabilen görünümde gerekir.
  useEffect(() => {
    if (!yazabilir) return undefined;
    let ignore = false;
    getMeslekList(0)
      .then((liste) => { if (!ignore) setKatalog(liste || []); })
      .catch(() => { if (!ignore) setKatalog([]); });
    return () => { ignore = true; };
  }, [yazabilir]);

  const tutulan = meslekler.map((m) => Number(m.meslekId));
  const sertifikaOnayli = (meslekId) => (yuvalar || []).some((y) => Number(y.meslekId) === Number(meslekId) && y.belge?.status === 1);

  async function calistir(anahtar, is, mesaj) {
    setBusy(anahtar);
    try {
      await is();
      onChanged?.(mesaj);
    } catch (e) {
      onError?.(e.message);
    } finally {
      setBusy(null);
    }
  }

  const ekle = () => {
    const id = Number(secim);
    if (!id) return;
    const ad = katalog.find((m) => Number(m.meslekId) === id)?.ad ?? `#${id}`;
    setSecim("");
    calistir("ekle", () => addKisiMeslegi(personId, id), t("kisiMeslekleri.eklendi", { ad }));
  };
  const kaldir = (m) => {
    setSilinecek(null);
    calistir(`kaldir-${m.meslekId}`, () => removeKisiMeslegi(personId, m.meslekId), t("kisiMeslekleri.kaldirildi", { ad: m.meslekAdi }));
  };
  const onayla = (m) => calistir(`onayla-${m.meslekId}`, () => onaylaKisiMeslegi(personId, m.meslekId), t("kisiMeslekleri.onaylandi", { ad: m.meslekAdi }));

  // Firma yalnızca KENDİ eklediğini ve onaylı sertifikası olmayanı kaldırır (backend de reddeder: kisi.meslek_firma_eklemedi / kisi.meslek_onayli_belge_var).
  // `buFirmaEkledi` GET /meslekler'de, `buFirmada` kişi satırlarındaki `meslekler[]`de aynı şeyi söyler (bakılan firma ekledi).
  const kaldirilabilir = (m) => yazabilir && (personel || (m.kaynak === "FIRMA" && (m.buFirmaEkledi ?? m.buFirmada) === true && !sertifikaOnayli(m.meslekId)));

  const Cerceve = bare ? "div" : Card;
  const Govde = bare ? "div" : CardBody;
  return (
    <Cerceve className={bare ? undefined : "h-100"}>
      {!bare && (
        <CardHeader className="bg-transparent d-flex align-items-center gap-2">
          <Briefcase size={16} className="text-muted" />
          <span className="fw-semibold">{t("kisiMeslekleri.baslik")}</span>
        </CardHeader>
      )}
      <Govde>
        {meslekler.length === 0 ? (
          <div className="text-muted small mb-2">{t("kisiMeslekleri.yok")}</div>
        ) : (
          <ul className="list-unstyled mb-2">
            {meslekler.map((m, i) => {
              const km = kartMeslegiOf(kartSatiri, m.meslekId);
              // Onay bekleyen = personel henüz onaylamadı. Kart kararı biliniyorsa yalnızca kuralı hafifse anlamlı; bilinmiyorsa
              // (fuar seçili değil) onaysız her meslek işaretlenir ki personel onaylayabilsin.
              const onayBekliyor = !m.onayli && (km ? km.hafifKural : true);
              const kural = km?.belgeKurali ?? m.belgeKurali ?? null;
              const belgeler = kural?.belgeler || [];
              return (
                <li key={m.meslekId} className={`py-2 ${i > 0 ? "border-top" : ""}`}>
                  <div className="d-flex align-items-start gap-2 flex-wrap">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div className="fw-semibold">
                        {m.meslekAdi}
                        {m.meslekKaldirildi && <span className="text-muted fw-normal"> {t("kisiMeslekleri.kaldirilmis")}</span>}
                      </div>
                      {m.kategoriAdi && <div className="small text-muted">{m.kategoriAdi}</div>}
                      <div className="small text-muted">{meslekKaynakMetni(m, t)}</div>
                    </div>
                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      {onayBekliyor && (
                        <Badge color="warning" className="fw-normal" title={t("kisiMeslekleri.onayBekliyorIpucu")}>{t("kisiMeslekleri.onayBekliyor")}</Badge>
                      )}
                      {km && (km.basilabilir
                        ? <Badge color="success" className="fw-normal d-inline-flex align-items-center gap-1"><CheckCircle2 size={11} />{t("kisiMeslekleri.basilabilir")}</Badge>
                        : <Badge color="light" className="text-body border fw-normal">{t("kisiMeslekleri.basilamaz")}</Badge>)}
                      {personel && yazabilir && onayBekliyor && (
                        <Button color="primary" size="sm" disabled={busy != null} onClick={() => onayla(m)}>
                          {busy === `onayla-${m.meslekId}` ? <Spinner size="sm" /> : t("kisiMeslekleri.onayla")}
                        </Button>
                      )}
                      {kaldirilabilir(m) && (
                        <button
                          type="button" className="btn btn-sm p-1 border-0 lh-1 text-muted" disabled={busy != null}
                          title={t("kisiMeslekleri.kaldir")} aria-label={t("kisiMeslekleri.kaldirAria", { ad: m.meslekAdi })}
                          onClick={() => setSilinecek(m)}
                        >
                          {busy === `kaldir-${m.meslekId}` ? <Spinner size="sm" /> : <X size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                  {km && !km.basilabilir && (km.eksikler || []).length > 0 && (
                    <div className="small text-muted mt-1">{t("kisiMeslekleri.eksik", { v: km.eksikler.map(meslekEksikAdi).join(", ") })}</div>
                  )}
                  {belgeler.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                      <span className="small text-muted">{t("kisiMeslekleri.istenenBelgeler")}</span>
                      {belgeler.map((b) => <Badge key={b.fileTypeId} color="light" className="text-body border fw-normal">{b.ad}</Badge>)}
                    </div>
                  )}
                  {renderSlot && <div className="mt-2">{renderSlot(m)}</div>}
                </li>
              );
            })}
          </ul>
        )}

        {yazabilir && (
          <div className="d-flex align-items-center gap-2">
            <Input type="select" bsSize="sm" value={secim} disabled={busy != null || katalog.length === 0} onChange={(e) => setSecim(e.target.value)}>
              <option value="">{t("meslekSecici.meslekEkle")}</option>
              <MeslekSecenekleri katalog={katalog} haric={tutulan} />
            </Input>
            <Button color="primary" size="sm" disabled={!secim || busy != null} onClick={ekle}>
              {busy === "ekle" ? <Spinner size="sm" /> : t("kisiMeslekleri.ekle")}
            </Button>
          </div>
        )}

        {calisanBelgeleri.length > 0 && (
          <div className="d-flex flex-wrap align-items-center gap-1 mt-3">
            <span className="small text-muted">{t("kisiMeslekleri.calisanSeti")}</span>
            {calisanBelgeleri.map((b) => <Badge key={b.fileTypeId} color="light" className="text-body border fw-normal">{b.ad}</Badge>)}
          </div>
        )}
        {children}
      </Govde>

      <Modal isOpen={silinecek != null} toggle={() => setSilinecek(null)} centered>
        <ModalHeader toggle={() => setSilinecek(null)}>{t("kisiMeslekleri.kaldirOnayBaslik")}</ModalHeader>
        <ModalBody>{silinecek && t("kisiMeslekleri.kaldirOnayMetin", { ad: silinecek.meslekAdi })}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSilinecek(null)}>{t("common:iptal")}</Button>
          <Button color="danger" onClick={() => kaldir(silinecek)}>{t("kisiMeslekleri.kaldir")}</Button>
        </ModalFooter>
      </Modal>
    </Cerceve>
  );
}
